/**
 * Title-family heuristics for first-pass triage (before full CV evaluation).
 * Tags are mutually exclusive priority: sales > commercial > ic_engineer > cto_like > other.
 */

export type TitleFitTag =
  | "cto_like"
  | "commercial"
  | "ic_engineer"
  | "sales"
  | "other";

const SALES_RE =
  /\b(account executive|account manager|ae\b|bdr|sdr|sales development|relationship manager|\brm\b|wealth advisor|financial advisor|business development|bd manager|hunter)\b/i;

const COMMERCIAL_RE =
  /\b(managing director|md\b|general manager|commercial|revenue|go.to.market|gtm|partnerships|chief commercial)\b/i;

const IC_ENGINEER_RE =
  /\b(software engineer|senior engineer|staff engineer|principal engineer|frontend|backend|full.?stack|sre|devops engineer|data engineer|ml engineer|ic\b)\b/i;

const CTO_LIKE_RE =
  /\b(cto|chief technology|chief information|cio\b|chief digital|vp\.?,?\s*(of\s+)?(technology|engineering|product technology|architecture)|head of (technology|engineering|architecture|product & technology|advisor technology)|director.*(technology|engineering|architecture)|md,?\s*ai|emerging technolog)/i;

export function classifyTitleFit(role: string): TitleFitTag {
  const title = (role ?? "").trim();
  if (!title) return "other";
  if (SALES_RE.test(title)) return "sales";
  if (COMMERCIAL_RE.test(title)) return "commercial";
  if (IC_ENGINEER_RE.test(title)) return "ic_engineer";
  if (CTO_LIKE_RE.test(title)) return "cto_like";
  return "other";
}

/** Obvious non-fits for a CTO-track first pass (safe to suggest bulk No-go). */
export function isObviousNonMatchForCto(role: string): boolean {
  const tag = classifyTitleFit(role);
  return tag === "sales" || tag === "ic_engineer";
}

export const TITLE_FIT_FILTER_OPTIONS: Array<{
  value: TitleFitTag | "";
  label: string;
}> = [
  { value: "", label: "All title fits" },
  { value: "cto_like", label: "CTO-shaped" },
  { value: "commercial", label: "Commercial / MD" },
  { value: "ic_engineer", label: "IC engineer" },
  { value: "sales", label: "Sales / BD / RM" },
  { value: "other", label: "Other" },
];

export function matchesTitleFitFilter(
  role: string,
  filter: TitleFitTag | "",
): boolean {
  if (!filter) return true;
  return classifyTitleFit(role) === filter;
}
